<template>
  <div class="card">
    <!-- Existing content -->
    <div v-if="loading" class="loading-overlay">
      <div class="spinner"></div>
    </div>
    <div class="card-header">
      <h2>{{ contentListTableInfo.title }}</h2>
      <button @click.prevent="showCreateDialog = true" class="btn btn-primary">
        Create Content
      </button>
    </div>
    <div class="card-subtitle">{{ contentListTableInfo.subTitle }}</div>
    <div class="table-responsive">
      <table class="modern-table" v-if="!contentLoading">
        <thead>
          <tr>
            <th
              v-for="column in contentListTableInfo.columns"
              :key="column.value"
            >
              {{ column.text }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="content in contentListTableInfo.data"
            :key="content.id"
            :class="[
              deleteContentId == content?.id ? 'selectedDeletionContent' : '',
              viewContentData?.id == content?.id ? 'selectedReviewContent' : '',
              editContentData?.id == content?.id
                ? 'selectedInitialContent'
                : '',
            ]"
          >
            <td>{{ content.id }}</td>
            <td>{{ truncateText(content.title, 30) }}</td>
            <td>{{ truncateText(content.description, 30) }}</td>
            <td>
              <p id="type" style="align-items: center; display: flex; gap: 4px">
                <i v-if="content.type === 'banner'" class="fas fa-image"></i>
                <i v-if="content.type === 'video'" class="fas fa-video"></i>
                {{ content.type }}
              </p>
            </td>
            <td>{{ formatDate(content.createdDate) }}</td>
            <td>
              <button @click="viewContentT(content)" class="icon-button">
                <i class="fas fa-eye"></i>
              </button>
              <button @click="editContentT(content)" class="icon-button">
                <i class="fas fa-edit"></i>
              </button>
              <button @click="deleteContentT(content?.id)" class="icon-button">
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="loading-indicator">
        <div class="spinner"></div>
      </div>
    </div>
    <div class="pagination">
      <button @click="prevPage" :disabled="currentPage === 1">Previous</button>
      <span>Page {{ currentPage }} of {{ totalPages }}</span>
      <button @click="nextPage" :disabled="currentPage === totalPages">
        Next
      </button>
    </div>
    <CreateContentDialog
      v-if="showCreateDialog"
      :openDialog="showCreateDialog"
      @close="showCreateDialog = false"
      @submit="
        showCreateDialog = false;
        getContent();
      "
    >
    </CreateContentDialog>
    <ReviewContentDialog
      v-if="viewContentDialog"
      :openDialog="viewContentDialog"
      :reviewContentData="viewContentData"
      @close="
        viewContentDialog = false;
        viewContentData = null;
      "
    >
    </ReviewContentDialog>
    <UpdateContentDialog
      v-if="editContentDialog"
      :openDialog="editContentDialog"
      :initialContentData="editContentData"
      @close="
        editContentDialog = false;
        editContentData = null;
      "
      @update="
        editContentDialog = false;
        editContentData = null;
        getContent();
      "
    >
    </UpdateContentDialog>
    <DeleteContentDialog
      v-if="deleteContentDialog"
      :openDialog="deleteContentDialog"
      :id="deleteContentId"
      @confirm-delete="
        deleteContentDialog = false;
        deleteContentId = null;
        getContent();
      "
      @cancel-delete="
        deleteContentDialog = false;
        deleteContentId = null;
      "
    >
    </DeleteContentDialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import dayjs from "dayjs";
import axios from "axios";

import { getBaseUrl } from "../helper/constant.js";

// Components
import CreateContentDialog from "../components/CreateContentDialog.vue";
import ReviewContentDialog from "../components/ReviewContentDialog.vue";
import UpdateContentDialog from "../components/UpdateContentDialog.vue";
import DeleteContentDialog from "../components/DeleteContentDialog.vue";

// Dialog
const showCreateDialog = ref(false);

// Content
const contentLoading = ref(false);
const contentList = ref([]);
const contentListTableInfo = ref({
  title: "Content Table",
  subTitle: "",
  columns: [
    { text: "ID", value: "id" },
    { text: "Title", value: "title" },
    { text: "Description", value: "description" },
    { text: "Type", value: "type" },
    { text: "Created", value: "createdDate" },
    { text: "Actions", value: "actions" },
  ],
  data: contentList.value,
});

// Pagination
const currentPage = ref(1);
const pageSize = ref(10);
const totalItems = ref(0);

const totalPages = computed(() => Math.ceil(totalItems.value / pageSize.value));

// Content detail
const loading = ref(false);
const contentDetailItem = ref(null);

async function getContent(skip = 0, take = 10) {
  if (contentLoading.value) return;
  contentLoading.value = true;
  contentList.value = [];

  try {
    const { data } = await axios.get(getBaseUrl() + `/core/content`, {
      params: { skip, take },
    });
    contentList.value = data.list;
    totalItems.value = data.count;
  } catch (er) {
    console.log(er);
  } finally {
    contentLoading.value = false;
  }
}
async function getContentDetail(id) {
  if (loading.value) return;
  loading.value = true;

  contentDetailItem.value = null;
  try {
    let url = getBaseUrl() + `/core/content-by-id`;
    const params = new URLSearchParams();
    params.append("id", id.toString());
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    contentDetailItem.value = (await axios.get(url)).data;
  } catch (err) {
    console.log(err);
    window.alert(
      "There was an error get the content. Please Content a developer",
    );
  } finally {
    loading.value = false;
    return contentDetailItem.value;
  }
}
const formatDate = (dateString) => {
  return dayjs(dateString).format("YYYY.MM.DD HH:mm");
};
function truncateText(text, length) {
  if (!text || !length) return "";
  if (text.length <= length) {
    return text;
  }
  return text.substring(0, length) + "...";
}
// ViewContent
const viewContentDialog = ref(false);
const viewContentData = ref(null);
const viewContentT = (content) => {
  if (content.id) {
    getContentDetail(Number(content.id)).then((data) => {
      if (data) {
        viewContentDialog.value = true;
        viewContentData.value = data;
      }
    });
  }
};
// Update Content
const editContentDialog = ref(false);
const editContentData = ref(null);
const editContentT = (content) => {
  if (content.id) {
    getContentDetail(Number(content.id)).then((data) => {
      if (data) {
        editContentDialog.value = true;
        editContentData.value = data;
      }
    });
  }
};
// Delete Content
const deleteContentDialog = ref(false);
const deleteContentId = ref(null);
const deleteContentT = (id) => {
  if (id) {
    deleteContentDialog.value = true;
    deleteContentId.value = id;
  }
};

// Pagination methods
const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    getContent((currentPage.value - 1) * pageSize.value, pageSize.value);
  }
};
const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    getContent((currentPage.value - 1) * pageSize.value, pageSize.value);
  }
};
watch(contentList, (newValue) => {
  contentListTableInfo.value.data = newValue;
});
onMounted(() => {
  getContent();
});
</script>

<style scoped>
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
}

.loading-overlay .spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}

.selectedInitialContent {
  background-color: #ccffd1 !important;
}
.selectedReviewContent {
  background-color: #cce5ff !important;
}
.selectedDeletionContent {
  background-color: #f8d7da !important;
}
.icon-button {
  background: none;
  border: none;
  cursor: pointer;
  margin-right: 5px;
}
.icon-button i {
  font-size: 16px;
  color: #333;
}
.card {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 16px;
  margin: 16px 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.card-header h2 {
  margin: 0;
}
.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
}
.btn-primary {
  background-color: #007bff;
}
.table-responsive {
  overflow-x: auto;
}
.modern-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 16px;
}
.modern-table th,
.modern-table td {
  padding: 12px;
  border: 1px solid #ddd;
  text-align: left;
}
.modern-table th {
  background-color: #f8f8f8;
}
.modern-table tr:nth-child(even) {
  background-color: #f2f2f2;
}
.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
}
.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #007bff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 16px;
}
.pagination button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  margin: 0 8px;
}
.pagination span {
  margin: 0 8px;
}
</style>
